import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import { compose } from 'recompose';
import moment from 'moment';
import JsonapiClient from '@holidayextras/jsonapi-client';

import {
  Segment,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react';

import BasicReport from 'dyzio-shared-components/lib/reports/basic-report';
import InstagramReport from 'dyzio-shared-components/lib/reports/instagram-report';

import hasRESTClient from '../../hocs/has-rest-client';
import hasOrgansation from '../../hocs/has-organisation';
import ThemeSelector from '../../themes/theme-selector';

import AddAssetForm from '../../components/add-asset-form';

import sendLimitsExceededNotification from '../../utils/send-limits-exceeded-notifcation';
import { authService } from '../../utils/auth-service';

import { KASPERSKY } from '../../utils/private-data-whitelist';

const KASPERSKY_ID = 'ec3b6d8e-f9f0-42f4-9c09-294368cbe683';
const JOHN_LEWIS_ID = 'cfa6155e-0b5e-4ebc-8150-c8715217fdc8';
const THE_ROOM_ID = '0cc7aa37-af63-4f05-95ac-c42ded273c32';

const currentTheme = ThemeSelector.theme();

const daysOptions = [
  { label: '30 days', value: 30, id: 30 },
  { label: '90 days', value: 90, id: 90 },
  { label: '120 days', value: 120, id: 120 },
  { label: 'all days', value: -1, id: 'all' },
];

const loadingSegmentStyle = { height: 175 };
const bottomSpace = { height: 100, width: '100%' };
const shouldReloadOnPeriodFilter = arg => !_.isNull(arg.start) && !_.isNull(arg.end);
const REPORT_API_TIMEOUT = 20000;

const DEFAULT_PER_DAYS = 90;

class Report extends React.Component {
  constructor() {
    super();
    const reportPerDays = _.find(daysOptions, ['value', DEFAULT_PER_DAYS]);
    this.state = {
      type: 'basic',
      id: null,
      error: false,
      loading: true,
      data: null,
      copied: false,
      sort: 'values.totals.followersCount',
      groupBy: 'influencer',
      addingAsset: false,
      newAssetPlatform: null,
      reportPerDays,
      order: {
        basic: {
          field: null,
          value: null,
        },
      },
      filter: {
        basic: {
          platform: null,
          discussion: null,
        },
      },
      datePeriodFilter: {
        start: null,
        end: null,
      },
    };

    this.client = axios.create({
      baseURL: process.env.REACT_APP_REPORT_API_URL,
      timeout: REPORT_API_TIMEOUT,
    });

    this.restClient = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL,
      timeout: 5000,
      headers: {
        Authorization: `Bearer ${authService.getToken()}`,
      },
    });

    this.jsonClient = new JsonapiClient(process.env.REACT_APP_API_BASE_URL, {
      header: {
        Authorization: `Bearer ${authService.getToken()}`,
      },
    });

    this.notifyClient = axios.create({
      baseURL: process.env.REACT_APP_NOTIFY_API_URL,
      timeout: 2000,
      headers: {
        Authorization: `Bearer ${authService.getToken()}`,
      },
    });
  }

  componentDidMount() {
    const { id, campaignAPIClient, userInfo: { organisationId } } = this.props;
    const { type } = this.state;
    this.loadReport(id, type);
    this.setState({
      isLoadingTotals: true,
    });
    const requests = [
      campaignAPIClient.get(`/summary/tracked-assets?organisationId=${organisationId}`),
    ];

    return Promise.all(requests).then(([{ data: { data: { trackedAssetsCount } } }]) => this.setState({
      trackedAssetsCount,
      isLoadingTotals: false,
    }));
  }

  onSortChanged = (event, data) => {
    const { value } = data;
    this.setState({
      sort: value,
    });
  }

  onOrderChanged = (field, value) => {
    const { order } = this.state;
    order.basic = {
      field,
      value,
    };
    this.setState({
      order,
    });
  }

  onGroupByChanged = (event, data) => {
    const { value } = data;
    this.setState({
      groupBy: value,
    });
  }

  onPerDaysChanged = (event, option) => {
    this.setState({
      reportPerDays: option,
    }, this.loadReport);
  }

  onFilterChange = (event, data) => {
    const handle = _.get(data, 'children[2]');
    const { filter } = this.state;
    const { value } = data;
    const parts = _.split(value, '.');
    const val = parts.pop();
    const key = parts.pop();
    filter.basic[key] = val === 'reset' ? null : val;
    filter.instagramHandle = val === 'reset' ? undefined : handle;
    this.setState({
      filter,
    });
  }

  loadReport = () => {
    const { id } = this.props;
    const { type, datePeriodFilter, reportPerDays } = this.state;
    const shouldFilterByDate = shouldReloadOnPeriodFilter(datePeriodFilter);
    const params = { id, type };
    if (reportPerDays && reportPerDays.value !== -1) {
      params.days = reportPerDays.value;
    }
    this.setState({
      loading: true,
      error: false,
    });
    const dateParams = {
      startDate: shouldFilterByDate ? datePeriodFilter.start.format('YYYY-MM-DD') : undefined,
      endDate: shouldFilterByDate ? datePeriodFilter.end.format('YYYY-MM-DD') : undefined,
      ...params,
    };
    this.client.get('report', {
      params: shouldFilterByDate ? dateParams : params,
    })
      .then(response => {
        const { data } = response;
        if (type === 'basic') {
          const posts = _.filter(data.data.posts, (post => !_.isNull(post) && !_.isUndefined(post.trackedAsset)));
          const uniqueByUrl = _.uniqBy(posts, 'trackedAsset.assetURL');
          data.data.posts = uniqueByUrl;
        }
        this.setState({
          data, id, type, loading: false, error: false,
        });
      }).catch(e => {
        console.log(e);
        this.setState({
          loading: false, error: true,
        });
      });
  }

  switchReport = (event, data) => {
    const { value } = data;
    const { id } = this.state;
    window.open(`${currentTheme.shareSite}/report/${id}/${value}`, '_blank');
  }

  copyToClipboard = () => {
    this.setState({
      copied: true,
    });
  }

  toggleAddAssetPopup = (event, data) => {
    const { hasSentRoadblockNotifcation } = this.state;
    const { value } = data;
    if (!hasSentRoadblockNotifcation) {
      this.checkLimitsNotification();
    }
    this.setState({
      addingAsset: true,
      hasSentRoadblockNotifcation: true,
      newAssetPlatform: value,
    });
  }

  checkLimitsNotification = () => {
    const { restClient, userInfo: { organisationId, name, userId }, organisation } = this.props;
    const { trackedAssetsCount } = this.state;
    if (!trackedAssetsCount || !organisation) return;
    if (trackedAssetsCount < organisation.licenseTrackedAssetLimit) return;
    sendLimitsExceededNotification(restClient, {
      data: {
        name: `${name} `,
        organisationId,
        userId,
        organisationName: `${organisation.name} `,
        assetType: 'tracked assets',
        licenseSubscriptionType: organisation.licenseSubscriptionType,
        url: window.location.href,
      },
    });
  }

  assetPopupToggleHandler = () => {
    this.setState({
      addingAsset: false,
      newAssetPlatform: null,
    });
  }

  addAssetHandler = details => {
    const REQUEST_TIMEOUT = 2000;
    const { id } = this.state;
    this.setState({
      loading: true,
    });
    const trackedAsset = this.jsonClient.create('trackedAssets');
    trackedAsset.set('assetId', details.assetId);
    trackedAsset.set('assetURL', details.assetURL);
    trackedAsset.set('user', {
      type: 'users',
      id: authService.uid(),
    });
    trackedAsset.set('campaign', {
      type: 'campaigns',
      id,
    });
    trackedAsset.set('platform', details.platform);
    trackedAsset.set('assetType', details.type);
    trackedAsset.set('createdSource', 'CAMPAIGN');
    trackedAsset.sync().then(() => {
      this.jsonClient.get('campaigns', id).then(campaign => {
        campaign.relationships('trackedAssets').add(trackedAsset);
        campaign.sync().then(() => {
          this.assetPopupToggleHandler();
          setTimeout(() => {
            this.loadReport();
          }, REQUEST_TIMEOUT);
        });
      });
    });
  }

  deleteTrackedAsset = (id, callback) => {
    this.jsonClient.get('trackedAssets', id, (err, asset) => {
      if (asset && !err) {
        asset.delete(error => {
          if (error) {
            callback(error);
            return;
          }
          callback(null);
        });
      }
    });
  }

  patchCampaign = () => {
    const { data: { data: { posts } } } = this.state;
    const { id } = this.props;

    const patch = _.map(posts, post => ({
      id: post.trackedAsset.id,
      type: 'trackedAssets',
    }));

    return this.restClient.patch(`/campaigns/${id}/relationships/trackedAssets`, { data: patch });
  }

  deleteAssetCallback = (e, button) => {
    const { value } = button;
    const { data } = this.state;
    const posts = _.reject(data.data.posts, ['trackedAsset.id', value]);
    this.setState({
      data: { data: { posts } },
    }, () => {
      this.patchCampaign().then(() => {
        this.deleteTrackedAsset(value);
      });
    });
  }

  handleDownloadClick = () => {
    const { data } = this.state;
    const profile = authService.getProfile();

    if (profile) {
      const campaign = {
        name: data.data.name,
      };
      const notification = {
        data: {
          profile,
          campaign,
          type: 'DOWNLOAD_CSV',
        },
      };
      this.notifyClient.post('/', notification);
    }
  }

  onDateFilterChange = (date, dateType) => {
    const { datePeriodFilter } = this.state;
    datePeriodFilter[dateType] = moment(date);
    this.setState({
      datePeriodFilter,
    }, () => {
      if (shouldReloadOnPeriodFilter(this.state.datePeriodFilter)) {
        this.loadReport();
      }
    });
  }

  render() {
    const {
      organisation,
      userInfo,
    } = this.props;
    const {
      type,
      data,
      error,
      sort,
      groupBy,
      filter,
      order,
      addingAsset,
      newAssetPlatform,
      isLoadingTotals,
      trackedAssetsCount,
      reportPerDays,
    } = this.state;

    const { isFacebookAdsEnabled, isGoogleAnalyticsEnabled, licenseSubscriptionType } = organisation || {};
    const isPremiumAccount = !_.isEqual(licenseSubscriptionType, 'FREE');

    const isKaspersky = _.get(userInfo, 'organisationId', null) === KASPERSKY_ID;
    const isJL = _.get(userInfo, 'organisationId') === JOHN_LEWIS_ID;
    const isTheRoom = _.get(userInfo, 'organisationId') === THE_ROOM_ID;

    const campaignType = this.state.loading ? null : _.get(data, 'data.campaignType');

    const isACR = campaignType === 'ACR';

    const loading = this.state.loading || this.props.organisationLoading || this.state.isLoadingTotals;

    const acrOptions = [
      {
        onClick: this.switchReport,
        key: 'basic',
        icon: 'file text outline',
        text: 'Campaign report',
        value: 'campaign',
        disable: false,
      },
      {
        onClick: this.switchReport,
        key: 'instagram',
        icon: 'instagram',
        text: 'Instagram report',
        value: 'instagram',
        disable: false,
      },
    ];

    if (isJL || isTheRoom) {
      acrOptions.push(
        {
          onClick: this.switchReport,
          key: 'instagram-custom',
          icon: 'instagram',
          text: 'Custom Instagram Report',
          value: 'instagram-custom',
          disable: !isPremiumAccount,
        },
      );
    }

    const otherOptions = [
      {
        onClick: this.switchReport,
        key: 'insights',
        icon: 'table',
        text: 'Insights report',
        value: 'insights',
        disable: true,
      },
      {
        onClick: this.switchReport,
        key: 'authorisation',
        icon: 'lock',
        text: 'Authorisation report',
        value: 'auth',
        disable: !isPremiumAccount,
      },
      {
        onClick: this.switchReport,
        key: 'roi',
        icon: 'dollar',
        text: 'Influencer ROI report',
        value: 'roi',
        disable: false,
      },
      {
        onClick: this.switchReport,
        key: 'paid',
        icon: 'money bill alternate outline',
        text: 'Paid report',
        value: 'paid',
        disable: !isFacebookAdsEnabled,
      },
      {
        onClick: this.switchReport,
        key: 'emv',
        icon: 'calculator',
        text: 'Earned Media Value (EMV)',
        value: 'emv',
        disable: false,
      },
      {
        onClick: this.switchReport,
        key: 'gi',
        icon: 'line graph',
        text: 'Guaranteed Impression Report',
        value: 'gi',
        disable: !isFacebookAdsEnabled,
      },
      {
        onClick: this.switchReport,
        key: 'ga',
        icon: 'google',
        text: 'Google Analytics Report',
        value: 'ga',
        disable: !isGoogleAnalyticsEnabled,
      },
    ];

    const options = isACR ? acrOptions : _.concat(acrOptions, otherOptions);

    const assetOptions = [
      {
        label: 'facebook post',
        icon: 'facebook',
        value: 'facebook',
        color: 'red',
        onClick: this.toggleAddAssetPopup,
      },
      {
        label: 'twitter post',
        icon: 'twitter',
        value: 'twitter',
        onClick: this.toggleAddAssetPopup,
      },
      {
        label: 'instagram post',
        icon: 'instagram',
        value: 'instagram',
        onClick: this.toggleAddAssetPopup,
      },
      {
        label: 'youtube video',
        icon: 'youtube',
        value: 'youtube',
        onClick: this.toggleAddAssetPopup,
      },
    ];

    const { datePeriodFilter: filterDates } = this.state;
    const datePeriodFilterValues = {
      start: filterDates.start && filterDates.start.format('DD/MM/YYYY'),
      end: filterDates.end && filterDates.end.format('DD/MM/YYYY'),
    };

    console.log('DO NOT AGGREGATE YOUTUBE');
    return (
      <div>
        { loading && <Segment style={loadingSegmentStyle}>
          <Dimmer active inverted>
            <Loader size="big">Loading</Loader>
          </Dimmer>
        </Segment>
        }
        { !loading && _.lowerCase(type) === 'basic' &&
          <BasicReport
            data={data}
            sort={sort}
            reportMode="internal"
            order={order.basic}
            filter={filter.basic}
            onFilterChange={this.onFilterChange}
            onOrderChanged={this.onOrderChanged}
            rightFilterOptions={_.reject(options, o => o.disable)}
            deleteCallback={this.deleteAssetCallback}
            hasRightFilterOptions={isKaspersky}
            assetOptions={assetOptions}
            onDownloadClick={this.handleDownloadClick}
            emptyCampaignText="You are not tracking any assets.
              Please go to the Influencer tab and click on each influencer, to add assets manually to your campaign. "
            withHeader={false}
            withDatePeriodFilter
            withDaysFilter
            daysOptions={daysOptions}
            perDaysOption={reportPerDays}
            onPerDaysChanged={this.onPerDaysChanged}
            datePeriodFilterValues={datePeriodFilterValues}
            onDateFilterChange={this.onDateFilterChange}
            instagramHandleFilter={_.get(this.state, 'filter.instagramHandle')}
          />
        }
        { !loading && !error && _.lowerCase(type) === 'instagram' && <div><br /><InstagramReport
          data={data}
          sort={sort}
          reportMode="internal"
          groupBy={groupBy}
          onSortChanged={this.onSortChanged}
          onGroupByChanged={this.onGroupByChanged}
          withHeader={false}
        /></div>
        }
        {/* { !loading && error && <Message
          error
          icon="warning circle"
          header="Something went wrong"
          content="Could not load report. Please try again later"
        />
        } */}
        {
          addingAsset && !loading && !isLoadingTotals && <AddAssetForm
            isOpen={addingAsset}
            isOverLimit={trackedAssetsCount >= organisation.licenseTrackedAssetLimit}
            platform={newAssetPlatform}
            popupToggleHandler={this.assetPopupToggleHandler}
            addAssetHandler={this.addAssetHandler}
            posts={data}
          />
        }
        <div style={bottomSpace} />
      </div>
    );
  }
}

Report.propTypes = {
  id: PropTypes.string.isRequired,
  organisationLoading: PropTypes.bool.isRequired,
  organisation: PropTypes.object,
  userInfo: PropTypes.object.isRequired,
};
Report.defaultProps = {
  organisation: null,
};

export default compose(hasRESTClient, hasOrgansation)(Report);



/** WEBPACK FOOTER **
 ** ./src/components/report/index.js
 **/